import {
  type ProductSlug,
  type ProductUrlSlug,
  PRODUCT_CATALOG,
  ALL_PRODUCTS,
  isProductSlug,
} from './products'

export interface ProductResolution {
  input: string
  normalizedInput: string
  sanitizedInput: string
  canonicalSlug: ProductSlug | null
  canonicalDisplayName: string | null
  recognized: boolean
}

const PRODUCT_SLUG_BY_URL = new Map<ProductUrlSlug, ProductSlug>(
  Object.entries(PRODUCT_CATALOG).map(([metadataKey, config]) => [
    config.urlSlug,
    metadataKey,
  ])
)

const PRODUCT_URL_SLUGS: readonly ProductUrlSlug[] = Object.values(PRODUCT_CATALOG).map(
  (product) => product.urlSlug
)

const PRODUCT_URL_SLUG_SET = new Set<ProductUrlSlug>(PRODUCT_URL_SLUGS)

const sanitizeProductKey = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const MANUAL_ALIASES: ReadonlyArray<[string, ProductSlug]> = [
  ['pallets', 'pallet'],
  ['palletlisting', 'pallet'],
  ['gaylordboxes', 'gaylord'],
  ['gaylordbox', 'gaylord'],
  ['gaylordboxeslisting', 'gaylord'],
  ['ibctotes', 'ibctote'],
  ['ibctoteslisting', 'ibctote'],
  ['ibctotecontainers', 'ibctote'],
  ['ibctotecontainer', 'ibctote'],
  ['ibctotecontainerswhite', 'ibctote'],
  ['ibctotecontainersblack', 'ibctote'],
  ['ibctotecontainerwhite', 'ibctote'],
  ['ibctotecontainerblack', 'ibctote'],
  ['metaldrums', 'metaldrum'],
  ['metaldrumlisting', 'metaldrum'],
  ['metaldrumslisting', 'metaldrum'],
  ['plasticdrums', 'plasticdrum'],
  ['plasticdrumlisting', 'plasticdrum'],
  ['plasticdrumslisting', 'plasticdrum'],
  ['woodcrates', 'woodcrate'],
  ['woodcrateslisting', 'woodcrate'],
  ['woodencrates', 'woodcrate'],
  ['plasticpallets', 'plasticpallet'],
  ['plasticpalletlisting', 'plasticpallet'],
  ['wooden spools', 'woodenspool'],
  ['woodenspools', 'woodenspool'],
  ['woodenspoolslisting', 'woodenspool'],
  ['plasticcrates', 'plasticcrate'],
  ['plasticcrateslisting', 'plasticcrate'],
  ['plasticsheds', 'plasticcrate'],
  ['bulkbags', 'bulkbag'],
  ['bulkbagslisting', 'bulkbag'],
  ['cardboardbales', 'cardboardbale'],
  ['cardboardbaleslisting', 'cardboardbale'],
  ['shippingboxes', 'shippingbox'],
  ['shippingboxeslisting', 'shippingbox'],
  ['movingboxes', 'movingbox'],
  ['movingboxeslisting', 'movingbox'],
]

const buildAliasMap = (): Map<string, ProductSlug> => {
  const map = new Map<string, ProductSlug>()

  const register = (alias: string, slug: ProductSlug) => {
    const sanitized = sanitizeProductKey(alias)
    if (!sanitized || map.has(sanitized)) return
    map.set(sanitized, slug)
  }

  for (const product of ALL_PRODUCTS) {
    register(product.slug, product.slug)
    register(product.urlSlug, product.slug)
    register(product.name, product.slug)
    register(product.urlSlug.replace(/[\s_-]+/g, ''), product.slug)
    register(product.slug.replace(/[\s_-]+/g, ''), product.slug)
    if (product.pluralName) {
      register(product.pluralName, product.slug)
    }
  }

  MANUAL_ALIASES.forEach(([alias, slug]) => register(alias, slug))

  return map
}

const CANONICAL_PRODUCT_SLUG_BY_ALIAS = buildAliasMap()

export function getMetadataKeyFromUrlSlug(urlSlug: string): ProductSlug | undefined {
  const normalized = urlSlug.trim().toLowerCase()
  return PRODUCT_SLUG_BY_URL.get(normalized)
}

export function getAllProductUrlSlugs(): readonly ProductUrlSlug[] {
  return PRODUCT_URL_SLUGS
}

export function isProductUrlSlug(value: unknown): value is ProductUrlSlug {
  if (typeof value !== 'string') return false
  const normalized = value.trim().toLowerCase()
  if (!normalized) return false
  return PRODUCT_URL_SLUG_SET.has(normalized)
}

function toResolution(
  input: string,
  normalizedInput: string,
  sanitizedInput: string,
  match: { slug: ProductSlug; name: string } | null,
): ProductResolution {
  return {
    input,
    normalizedInput,
    sanitizedInput,
    canonicalSlug: match?.slug ?? null,
    canonicalDisplayName: match?.name ?? null,
    recognized: match !== null,
  }
}

function matchProduct(normalizedInput: string, sanitizedInput: string) {
  const slugFromAlias = sanitizedInput ? CANONICAL_PRODUCT_SLUG_BY_ALIAS.get(sanitizedInput) : undefined
  if (slugFromAlias) return { slug: slugFromAlias, name: PRODUCT_CATALOG[slugFromAlias].name }
  const displayMatch = ALL_PRODUCTS.find(
    (product) => product.name.toLowerCase() === normalizedInput.toLowerCase()
  )
  return displayMatch ? { slug: displayMatch.slug, name: displayMatch.name } : null
}

export function resolveCanonicalProductType(value: string | null | undefined): ProductResolution {
  if (typeof value !== 'string') return toResolution('', '', '', null)
  const normalizedInput = value.trim()
  const sanitizedInput = normalizedInput ? sanitizeProductKey(normalizedInput) : ''
  if (!normalizedInput) return toResolution(value, normalizedInput, sanitizedInput, null)
  return toResolution(value, normalizedInput, sanitizedInput, matchProduct(normalizedInput, sanitizedInput))
}

export function resolveProductUrlSlug(value: string): ProductUrlSlug | null {
  if (typeof value !== 'string') return null
  const normalized = value.trim().toLowerCase()
  if (!normalized) return null

  if (PRODUCT_URL_SLUG_SET.has(normalized)) {
    return normalized
  }

  const resolution = resolveCanonicalProductType(value)
  if (resolution.canonicalSlug) {
    const product = PRODUCT_CATALOG[resolution.canonicalSlug]
    if (product) {
      return product.urlSlug
    }
  }

  return null
}

export function normalizeProductSlug(input: string): string {
  const resolution = resolveCanonicalProductType(input)
  if (resolution.canonicalSlug) return resolution.canonicalSlug
  if (resolution.sanitizedInput) return resolution.sanitizedInput
  return resolution.normalizedInput.toLowerCase()
}

export function denormalizeProductSlug(slug: string): string {
  const sanitized = sanitizeProductKey(slug)
  const resolvedSlug =
    CANONICAL_PRODUCT_SLUG_BY_ALIAS.get(sanitized) ?? (isProductSlug(slug) ? slug : null)

  if (resolvedSlug) {
    const product = PRODUCT_CATALOG[resolvedSlug]
    return product?.urlSlug ?? slug.trim().toLowerCase().replace(/\s+/g, '-')
  }

  return slug.trim().toLowerCase().replace(/\s+/g, '-')
}
